import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  CalendarData,
  CalendarEvent,
  EventCalendarResult,
  createEventCalendar,
  deleteEventCalendar,
  fetchCalendarData,
  fetchPatientContact,
  updateEventCalendar,
} from '../../api/calendar'
import { MultipleAgendaProcessor } from '../../lib/agenda'
import { ApiError } from '../../lib/api'
import { useIsAccountClosed } from '../../components/AppShell'
import { trackEvent } from '../../lib/config'
import { checkRut, formatRut } from '../../lib/rut'
import { ButtonAnchor, Modal, RutField, SubmitButton, TextField, useConfirm, useToast } from '../../components/common'
import { SearchableSelect, SearchableOption } from '../../components/common'
import styles from './AppointmentModal.module.scss'

const LOCKED_COLOR = '#b1b1b1'
const UNLOCKED_COLOR = '#619cd2'
const CREATE_PATIENT_OPTION: SearchableOption = { value: '-1', label: 'Crear nuevo paciente' }
const PATIENT_INFO_FIELDS = [
  { key: 'name', id: 'patient-info-name', label: 'Nombre' },
  { key: 'rut', id: 'patient-info-rut', label: 'RUT' },
  { key: 'email', id: 'patient-info-email', label: 'Email' },
  { key: 'phone_number', id: 'patient-info-phone', label: 'Teléfono' },
] as const
const APPOINT_BY_OPTIONS: readonly SearchableOption[] = [
  { value: 'professional', label: 'Profesional determinado' },
  { value: 'date', label: 'Fecha determinada' },
]

function toDmy(isoDate: string): string {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function CircleField({
  number,
  locked,
  filled,
  children,
  onLockedClick,
}: {
  number: number
  locked: boolean
  filled: boolean
  children: ReactNode
  onLockedClick: () => void
}) {
  const color = locked ? LOCKED_COLOR : UNLOCKED_COLOR
  return (
    <div className={`${styles.selectOption} label-static`}>
      <p
        className={styles.circleNumber}
        style={
          filled && !locked
            ? { color: 'white', border: `2px solid ${color}`, background: color }
            : { color, border: `2px solid ${color}`, background: 'white' }
        }
      >
        {number}
      </p>
      <div className={styles.inputBox} onClick={locked ? onLockedClick : undefined}>
        {children}
      </div>
    </div>
  )
}

export function AppointmentModal({
  healthCenterId,
  data,
  processor,
  event,
  initialDate,
  onClose,
  onSaved,
  onDeleted,
}: {
  healthCenterId: number
  data: CalendarData
  processor: MultipleAgendaProcessor
  event: CalendarEvent | null
  initialDate: string | null
  onClose: () => void
  onSaved: (result: EventCalendarResult) => void
  onDeleted: (id: string, message: string) => void
}) {
  const showToast = useToast()
  const confirm = useConfirm()
  const closed = useIsAccountClosed()
  const [patientId, setPatientId] = useState<string>(event ? String(event.patient_id) : '')
  const [patientInfo, setPatientInfo] = useState({ name: '', rut: '', email: '', phone_number: '' })
  const [appointBy, setAppointBy] = useState<'' | 'professional' | 'date'>(event ? 'professional' : '')
  const [professionalId, setProfessionalId] = useState<string>(event ? String(event.professional_id) : '')
  const [dateIso, setDateIso] = useState<string>(() => {
    if (event) {
      return toIso(new Date(event.start))
    }
    if (initialDate) {
      return initialDate
    }
    return ''
  })
  const [duration, setDuration] = useState<number>(() => {
    if (event) {
      return Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000)
    }
    return data.session_durations[0] ?? data.session_duration
  })
  const [time, setTime] = useState<string>(event ? event.time : '')
  const [description, setDescription] = useState<string>(event ? event.description : '')
  const [submitting, setSubmitting] = useState(false)

  const isNewPatient = patientId === '-1'
  const dateDmy = dateIso ? toDmy(dateIso) : ''

  useEffect(() => {
    if (event) {
      setPatientInfo({ name: event.patient_name, rut: event.patient_rut, email: event.patient_email, phone_number: event.patient_phone })
    }
  }, [event])

  useEffect(() => {
    if (patientId && patientId !== '-1') {
      void fetchPatientContact(healthCenterId, patientId).then((contact) => {
        setPatientInfo({
          name: contact.name ?? '',
          rut: contact.rut ?? '',
          email: contact.email ?? '',
          phone_number: contact.phone_number ?? '',
        })
      })
    }
  }, [healthCenterId, patientId])

  const agendaProcessor = professionalId ? processor.getAgenda(professionalId) : undefined

  const professionalOptions = useMemo(() => {
    if (appointBy === 'date' && dateDmy) {
      return processor.professionalsForDate(dateDmy)
    }
    return Object.values(processor.professionals)
  }, [processor, appointBy, dateDmy])

  const patientOptions: SearchableOption[] = useMemo(
    () =>
      data.patients.map((patient) => ({
        value: String(patient.id),
        label: patient.rut ? `${patient.name} (${patient.rut})` : patient.name,
      })),
    [data.patients],
  )

  const professionalSelectOptions: SearchableOption[] = useMemo(
    () => professionalOptions.map((professional) => ({ value: String(professional.id), label: professional.name })),
    [professionalOptions],
  )

  const timeOptions = useMemo(() => {
    if (!agendaProcessor || !dateDmy) {
      return []
    }
    const options = agendaProcessor.timeOptionsFor(dateDmy, duration)
    if (event && !options.includes(event.time)) {
      return [event.time, ...options].sort()
    }
    return options
  }, [agendaProcessor, dateDmy, duration, event])

  const durationOptions = useMemo(() => {
    const options = [...data.session_durations]
    if (event && duration > 0 && !options.includes(duration)) {
      options.push(duration)
    }
    return [...new Set(options)]
  }, [data.session_durations, event, duration])

  const durationSelectOptions: SearchableOption[] = useMemo(
    () => durationOptions.map((option) => ({ value: String(option), label: `${option} min` })),
    [durationOptions],
  )

  const timeSelectOptions: SearchableOption[] = useMemo(
    () => timeOptions.map((option) => ({ value: option, label: option })),
    [timeOptions],
  )

  const patientStepDone = (patientId !== '' && patientId !== '-1') || (isNewPatient && patientInfo.name !== '' && patientInfo.rut !== '')
  const appointByUnlocked = patientStepDone
  const professionalUnlocked =
    patientStepDone && ((appointBy === 'professional') || (appointBy === 'date' && dateDmy !== ''))
  const dateUnlocked =
    patientStepDone && ((appointBy === 'date') || (appointBy === 'professional' && professionalId !== ''))
  const finalUnlocked =
    patientStepDone &&
    ((appointBy === 'professional' && professionalId !== '' && dateDmy !== '') ||
      (appointBy === 'date' && dateDmy !== '' && professionalId !== ''))

  const warnLocked = () => {
    trackEvent('Appointment Validation Failed', { reason: 'locked_step' })
    showToast('warning', 'Completa los campos anteriores para cambiar este valor')
  }

  const getCurrentStep = (): number => {
    if (!patientStepDone) {
      return 1
    }
    if (appointBy === '') {
      return 2
    }
    if (professionalId === '' || dateIso === '') {
      return appointBy === 'professional' && professionalId === '' ? 3 : 4
    }
    return 5
  }

  const handleClose = () => {
    trackEvent('Appointment Modal Abandoned', { is_new: event === null, step: getCurrentStep() })
    onClose()
  }

  const handleDateChange = (iso: string) => {
    if (iso === '') {
      setDateIso('')
      return
    }
    const dmy = toDmy(iso)
    if (appointBy === 'professional' && agendaProcessor && !agendaProcessor.hasTimesForDay(dmy)) {
      trackEvent('Appointment Validation Failed', { reason: 'no_slots' })
      showToast('warning', 'No hay horarios disponibles para ese día')
      setDateIso('')
      setTime('')
      return
    }
    if (appointBy === 'date' && processor.disabledDates(null).includes(iso)) {
      trackEvent('Appointment Validation Failed', { reason: 'no_slots' })
      showToast('warning', 'No hay horarios disponibles para ese día')
      setDateIso('')
      setTime('')
      return
    }
    setDateIso(iso)
    setTime('')
  }

  const handleSubmit = async () => {
    if (submitting || closed) {
      return
    }
    if (!patientStepDone || !finalUnlocked || time === '') {
      trackEvent('Appointment Validation Failed', { reason: 'missing_fields' })
      showToast('warning', 'Completa todos los campos requeridos')
      return
    }
    if (isNewPatient && patientInfo.rut !== '' && !checkRut(patientInfo.rut)) {
      trackEvent('Appointment Validation Failed', { reason: 'invalid_rut' })
      showToast('error', 'Rut inválido')
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        box_calendar_id: event ? event.box_calendar_id : (data.selected_box_id as number),
        patient_id: isNewPatient ? -1 : patientId,
        professional_id: professionalId,
        start_at_date: dateDmy,
        start_at_time: time,
        duration,
        description,
        ...(isNewPatient
          ? { patient_info: { ...patientInfo, rut: patientInfo.rut === '' ? patientInfo.rut : formatRut(patientInfo.rut) } }
          : {}),
      }
      const result = event
        ? await updateEventCalendar(healthCenterId, event.event_calendar_id, payload)
        : await createEventCalendar(healthCenterId, payload)
      completeSave(result)
    } catch (requestError) {
      if (requestError instanceof ApiError) {
        trackEvent('Appointment Save Failed', { reason: 'request_failed', status_code: requestError.status })
        showToast('error', 'Hubo un error al agendar la hora')
        setSubmitting(false)
        return
      }
      try {
        const saved = await findSavedAppointment()
        if (saved) {
          completeSave({ errors: null, event_calendar: saved })
          return
        }
        trackEvent('Appointment Save Failed', { reason: 'no_response' })
        showToast('error', 'Hubo un error al agendar la hora')
      } catch {
        trackEvent('Appointment Save Failed', { reason: 'unreachable' })
        showToast('error', 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.')
      }
      setSubmitting(false)
    }
  }

  // These two handlers signal failure by payload rather than by rejection, so the
  // failure event belongs on the errors branch, not only in the catch.
  const completeSave = (result: EventCalendarResult) => {
    if (result.errors) {
      trackEvent('Appointment Save Failed', { reason: result.errors })
      showToast('error', result.errors)
      setSubmitting(false)
      return
    }
    trackEvent(event ? 'Appointment Updated' : 'Appointment Created', {
      duration_minutes: duration,
      appoint_by: appointBy,
      professional_id: professionalId,
      is_new_patient: isNewPatient,
    })
    onSaved(result)
  }

  // A save the server answered but the browser never heard back from — a dropped
  // connection — may still have been stored, and a retry would then be refused
  // as a double booking, so the calendar is asked which of the two happened.
  const findSavedAppointment = async (): Promise<CalendarEvent | undefined> => {
    const { events } = await fetchCalendarData(healthCenterId, {
      box: '',
      professional: professionalId,
      patient: '',
      start: `${dateIso}T00:00:00`,
      end: `${dateIso}T23:59:59`,
    })
    return events.find((saved) => {
      if (String(saved.professional_id) !== professionalId || !saved.time.startsWith(time)) {
        return false
      }
      if (event) {
        return saved.event_calendar_id === event.event_calendar_id
      }
      return isNewPatient ? saved.patient_name === patientInfo.name : String(saved.patient_id) === patientId
    })
  }

  const handleDelete = async () => {
    if (!event || submitting) {
      return
    }
    const confirmed = await confirm({ title: 'Eliminar cita', message: '¿Estás seguro de que deseas eliminar esta cita?' })
    if (!confirmed) {
      return
    }
    setSubmitting(true)
    try {
      const result = await deleteEventCalendar(healthCenterId, event.event_calendar_id)
      if (result.errors) {
        trackEvent('Appointment Save Failed', { reason: result.errors })
        showToast('error', result.errors)
      } else if (result.id) {
        trackEvent('Appointment Deleted')
        onDeleted(result.id, result.message)
      }
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { errors?: string } | null) : null
      trackEvent('Appointment Save Failed', {
        reason: body?.errors ?? 'request_failed',
        status_code: requestError instanceof ApiError ? requestError.status : undefined,
      })
      showToast('error', body?.errors ?? 'No pudimos eliminar la cita, inténtalo nuevamente')
    }
    setSubmitting(false)
  }

  return (
    <Modal open onClose={handleClose} id="modal_appointment" size="wide" title={event ? 'Editar cita' : 'Agendar hora'}>
      <form
                id="appointment-calendar-form"
                onSubmit={(formEvent) => {
                  formEvent.preventDefault()
                  void handleSubmit()
                }}
              >
                <div className={`row ${styles.newAppointment}`}>
                  <div className="col-xs-12 col-sm-12 col-md-4">
                    <h4 className="text-center blue-subtitle">Paciente</h4>
                    <div className={`${styles.selectOption} label-static`}>
                      <p className={styles.circleNumber} style={{ color: UNLOCKED_COLOR, border: `2px solid ${UNLOCKED_COLOR}`, background: 'white' }}>
                        1
                      </p>
                      <div className={styles.inputBox}>
                        <label className="control-label">Nombre o RUT</label>
                        <SearchableSelect
                          id="event_calendar_patient"
                          placeholder="Selecciona a tu paciente"
                          required
                          value={patientId}
                          options={patientOptions}
                          pinnedOption={CREATE_PATIENT_OPTION}
                          onChange={setPatientId}
                        />
                      </div>
                    </div>
                    <div className={styles.patientInfo} style={{ backgroundColor: isNewPatient ? 'white' : undefined }}>
                      {PATIENT_INFO_FIELDS.map(({ key, id, label }) => (
                        <div key={key}>
                          {key === 'rut' ? (
                            <RutField
                              id={id}
                              label={label}
                              labelStyle="static"
                              disabled={!isNewPatient}
                              value={patientInfo.rut}
                              onChange={(value) => setPatientInfo((current) => ({ ...current, rut: value }))}
                            />
                          ) : (
                            <TextField
                              id={id}
                              label={label}
                              labelStyle="static"
                              disabled={!isNewPatient}
                              value={patientInfo[key]}
                              onChange={(value) => setPatientInfo((current) => ({ ...current, [key]: value }))}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`col-xs-12 col-sm-12 col-md-4 ${styles.professionalInfo}`}>
                    <h4 className="text-center blue-subtitle">Fecha y Profesional</h4>
                    <CircleField number={2} locked={!appointByUnlocked} filled={appointBy !== ''} onLockedClick={warnLocked}>
                      <label className="control-label">Agendar cita para</label>
                      <SearchableSelect
                        id="appoint-by-selector"
                        placeholder="Agendar cita para..."
                        disabled={!appointByUnlocked}
                        onDisabledClick={warnLocked}
                        value={appointBy}
                        options={APPOINT_BY_OPTIONS}
                        onChange={(next) => {
                          setAppointBy(next as '' | 'professional' | 'date')
                          setProfessionalId('')
                          setTime('')
                        }}
                      />
                    </CircleField>

                    {appointBy === 'date' ? (
                      <>
                        <CircleField number={3} locked={!dateUnlocked} filled={dateIso !== ''} onLockedClick={warnLocked}>
                          <label className="control-label">Fecha de atención</label>
                          <input
                            type="date"
                            className="form-control required"
                            disabled={!dateUnlocked}
                            min={event ? undefined : toIso(new Date())}
                            value={dateIso}
                            onChange={(changeEvent) => handleDateChange(changeEvent.target.value)}
                          />
                        </CircleField>
                        <CircleField number={4} locked={!professionalUnlocked} filled={professionalId !== ''} onLockedClick={warnLocked}>
                          <label className="control-label">Profesional</label>
                          <SearchableSelect
                            id="event_calendar_professional_id"
                            placeholder="Selecciona un profesional"
                            required
                            disabled={!professionalUnlocked}
                            onDisabledClick={warnLocked}
                            value={professionalId}
                            options={professionalSelectOptions}
                            onChange={(next) => {
                              setProfessionalId(next)
                              setTime('')
                            }}
                          />
                        </CircleField>
                      </>
                    ) : (
                      <>
                        <CircleField number={3} locked={appointBy === '' || !professionalUnlocked} filled={professionalId !== ''} onLockedClick={warnLocked}>
                          <label className="control-label">Profesional</label>
                          <SearchableSelect
                            id="event_calendar_professional_id"
                            placeholder="Selecciona un profesional"
                            required
                            disabled={appointBy === '' || !professionalUnlocked}
                            onDisabledClick={warnLocked}
                            value={professionalId}
                            options={professionalSelectOptions}
                            onChange={(next) => {
                              setProfessionalId(next)
                              setTime('')
                            }}
                          />
                        </CircleField>
                        <CircleField number={4} locked={!dateUnlocked} filled={dateIso !== ''} onLockedClick={warnLocked}>
                          <label className="control-label">Fecha de atención</label>
                          <input
                            type="date"
                            className="form-control required"
                            autoComplete="off"
                            disabled={!dateUnlocked}
                            min={event ? undefined : toIso(new Date())}
                            value={dateIso}
                            onChange={(changeEvent) => handleDateChange(changeEvent.target.value)}
                          />
                        </CircleField>
                      </>
                    )}

                    <CircleField number={5} locked={!finalUnlocked} filled={duration > 0} onLockedClick={warnLocked}>
                      <label className="control-label">
                        Duración de la cita{' '}
                        <a
                          href={`/professionals/health_centers/${healthCenterId}/edit#calendar`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('Settings Opened', { section: 'schedule', source: 'appointment_modal' })}
                        >
                          (personalizar)
                        </a>
                      </label>
                      <SearchableSelect
                        id="event_calendar_duration"
                        placeholder="Duración"
                        required
                        disabled={!finalUnlocked}
                        onDisabledClick={warnLocked}
                        value={String(duration)}
                        options={durationSelectOptions}
                        onChange={(next) => {
                          setDuration(Number(next))
                          setTime('')
                        }}
                      />
                    </CircleField>

                    <CircleField number={6} locked={!finalUnlocked} filled={time !== ''} onLockedClick={warnLocked}>
                      <label className="control-label">Hora</label>
                      <SearchableSelect
                        id="event_calendar_professional_start_at_time"
                        placeholder={timeOptions.length === 0 && dateDmy !== '' ? 'No quedan horas disponibles' : 'Selecciona una hora'}
                        required
                        disabled={!finalUnlocked}
                        onDisabledClick={warnLocked}
                        value={time}
                        options={timeSelectOptions}
                        onChange={setTime}
                      />
                    </CircleField>
                  </div>

                  <div className="col-xs-12 col-sm-12 col-md-4">
                    <h4 className="text-center blue-subtitle">Información Adicional</h4>
                    <div className={`${styles.selectOption} label-static`}>
                      <p className={styles.circleNumber} style={{ color: finalUnlocked ? UNLOCKED_COLOR : LOCKED_COLOR, border: `2px solid ${finalUnlocked ? UNLOCKED_COLOR : LOCKED_COLOR}`, background: 'white' }}>
                        7
                      </p>
                      <div className={styles.inputBox}>
                        <label className="control-label">
                          <span>Descripción (opcional)</span>
                        </label>
                        <textarea
                          id="event_calendar_description"
                          rows={5}
                          className="form-control"
                          style={{ marginTop: 4, paddingBottom: 5 }}
                          disabled={!finalUnlocked}
                          value={description}
                          onChange={(changeEvent) => setDescription(changeEvent.target.value)}
                        />
                      </div>
                    </div>
                    {data.has_professional_without_schedule && (
                      <div className={styles.scheduleError}>
                        <i className="material-icons">warning</i>
                        <p>
                          Hay profesionales que no tienen asignado un horario de trabajo, por lo que no podrás agendarles consultas. Para
                          asignar el horario de trabajo de un profesional{' '}
                          <a
                            href={`/health_centers/${healthCenterId}/professionals`}
                            onClick={() => trackEvent('Settings Opened', { section: 'accounts', source: 'appointment_modal' }, { transport: 'sendBeacon' })}
                          >
                            haz click aquí.
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {!closed && (
                  <div className={`text-right ${styles.actions}`}>
                    <SubmitButton
                      label="Guardar"
                      id="submit-button"
                      disabled={!data.trial_features_enabled || submitting}
                    />
                    {event && (
                      <ButtonAnchor
                        href="#"
                        variant="danger"
                        id="delete-button"
                        onClick={(clickEvent) => {
                          clickEvent.preventDefault()
                          void handleDelete()
                        }}
                      >
                        Eliminar
                      </ButtonAnchor>
                    )}
                  </div>
                )}
      </form>
    </Modal>
  )
}
